import axios from 'axios';

// 生产走同源 '/api'（nginx 反代）；dev 直连 api 端口绕过 next proxy。
// 原因：next dev 的 rewrite proxy 有硬编码 30s 超时（next-server.ts 的 http
// proxy config），大文件上传（几百 MB）耗时长于 30s 会被掐断报 socket hang up。
// dev 直连 :3001 跳过该限制，CORS 已允许 3002、SameSite=Lax 同 site 能带 cookie。
// 生产环境由 nginx 反代（proxy_read_timeout 600s），不受此限制。
//
// 注意：不能用 NEXT_PUBLIC_API_BASE——它的语义是「不含 /api 的 origin」
// （next.config.js 的 rewrites 拿它当 destination 前缀自己拼 /api）。
// 而 axios baseURL 要含全局前缀 'api'（main.ts setGlobalPrefix），所以 dev 下
// 用固定值 http://localhost:3001/api。如需改端口走 NEXT_PUBLIC_API_URL（已含 /api）。
const API_BASE =
  process.env.NODE_ENV === 'production'
    ? '/api'
    : process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// refresh 状态机: 一次只跑一个 refresh,其余 401 在队列里等
let isRefreshing = false;
let pendingRequests: Array<{
  resolve: (value?: unknown) => void;
  reject: (err: unknown) => void;
}> = [];

function processQueue(error?: unknown) {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  pendingRequests = [];
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const url = error.config?.url || '';
    const isAuthEndpoint =
      url.startsWith('/auth/login') ||
      url.startsWith('/auth/register') ||
      url.startsWith('/auth/refresh') ||
      url.startsWith('/auth/me');

    if (error.response?.status !== 401 || error.config._retry || isAuthEndpoint) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingRequests.push({ resolve, reject });
      }).then(() => api(error.config));
    }

    error.config._retry = true;
    isRefreshing = true;
    try {
      await axios.post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true });
      processQueue();
      return api(error.config);
    } catch (refreshError) {
      // 排队中的请求全部 reject,避免永久悬挂
      processQueue(refreshError);
      if (typeof window !== 'undefined') window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
